import React, { ReactNode } from "react";
import ReactDOMServer from "react-dom/server";

type Props = {
  isDebug?: boolean;
  applicantCount?: number;
};

export const getVacancyContractSignatureHTML = (props?: Props) => {
  const isDebug = props?.isDebug || false;
  const applicantCount = props?.applicantCount || 1;
  return ReactDOMServer.renderToString(
    <VacancyContractSignature
      isDebug={isDebug}
      applicantCount={applicantCount}
    />
  );
};

const VacancyContractSignature = (props: Props) => {
  const isDebug = props?.isDebug || false;
  const applicantCount = props?.applicantCount || 1;
  const lastSigningIndex = applicantCount + 1;

  return (
    <div style={{ pageBreakInside: "avoid" }}>
      <br></br>
      {/* SIGNATURE CONTAINER */}
      <span
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <CompanySignaturePart
          signingIndex={lastSigningIndex}
          isDebug={isDebug}
        />
        <TenantSignaturePart
          isDebug={isDebug}
          applicantCount={applicantCount}
        />
      </span>
    </div>
  );
};

type CompanySignaturePartProps = {
  isDebug?: boolean;
  signingIndex: number;
};

const CompanySignaturePart = (props: CompanySignaturePartProps) => {
  const { isDebug = false, signingIndex = 2 } = props;

  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <span style={{ zIndex: 1 }}>
        <u>__________________________________</u>
      </span>
      <span
        style={{
          textAlign: "left",
          position: "absolute",
          top: -8,
          zIndex: 2,
          fontSize: "14px",
          width: "270px",
          background: isDebug ? "grey" : "transparent",
          color: "white",
        }}
      >
        <span style={{ fontSize: "14px", color: "#444444" }}>Paderborn, </span>
        {`{{@signdate_${signingIndex}}}`}
      </span>

      <span style={{ position: "relative", zIndex: 1 }}>Ort, Datum</span>
      <SignatureElement
        signReplaceComponent={
          <span
            className="mention-variable"
            data-variable-id="entityDisplayName"
          >
            $EntityDisplayName
          </span>
        }
        signingIndex={signingIndex}
        isDebug={isDebug}
        signerSubText="-Vermieter-"
      />
    </span>
  );
};

export type TenantSignaturePartProps = {
  applicantCount?: number;
  isDebug?: boolean;
};

const TenantSignaturePart = (props: TenantSignaturePartProps) => {
  const { isDebug = false, applicantCount = 1 } = props;

  return (
    <span style={{ display: "flex", flexDirection: "column" }}>
      {Array.from({ length: applicantCount }, (_, index) => {
        const signingIndex = index + 1;
        const emptyLines = index === 0 ? null : <br></br>;
        return (
          <span
            key={signingIndex}
            style={{
              display: "flex",
              flexDirection: "column",
              marginLeft: "auto",
            }}
          >
            {emptyLines}
            {emptyLines}
            {emptyLines}
            {emptyLines}
            <span
              style={{
                marginLeft: "auto",
                display: "flex",
                flexDirection: "column",
                textAlign: "right",
                position: "relative",
              }}
            >
              {/* Underline element */}
              <span style={{ position: "relative", zIndex: 1 }}>
                <u>_____________________________________</u>
              </span>
              <span
                style={{
                  textAlign: "left",
                  position: "absolute",
                  top: -8,
                  left: 0,
                  zIndex: 2,
                  fontSize: "14px",
                  width: "350px",
                  background: isDebug ? "grey" : "transparent",
                  color: "white",
                }}
              >
                {`{{`}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;text&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                {`${signingIndex}`}
                &nbsp;&nbsp;&nbsp;|*|&nbsp;&nbsp;&nbsp;Ort&nbsp;&nbsp;&nbsp;
                {`}}`} {`{{@signdate_${signingIndex}}}`}
              </span>
              <span style={{ position: "relative", zIndex: 1 }}>
                Ort, Datum
              </span>
              <SignatureElement
                alignRight
                isDebug={isDebug}
                signReplaceComponent={
                  <span style={{ marginLeft: "auto" }}>
                    <span
                      className="mention-variable"
                      data-variable-id={`applicantFirstName-${
                        signingIndex - 1
                      }`}
                    >
                      $Vacancy-Applicant-FirstName
                    </span>
                    &nbsp;
                    <span
                      className="mention-variable"
                      data-variable-id={`applicantLastName-${signingIndex - 1}`}
                    >
                      $Vacancy-Applicant-LastName
                    </span>
                  </span>
                }
                signerSubText="-Mieter-"
                signingIndex={signingIndex}
              />
            </span>
          </span>
        );
      })}
    </span>
  );
};

type SignatureElementProps = {
  isDebug?: boolean;
  signingIndex: number;
  signReplaceComponent: ReactNode;
  signerSubText: string;
  alignRight?: boolean;
};

const SignatureElement = (props: SignatureElementProps) => {
  const {
    signReplaceComponent,
    signerSubText,
    signingIndex,
    isDebug = false,
    alignRight = false,
  } = props;

  return (
    <p
      style={{
        marginTop: "50px",
        display: "flex",
        textAlign: "right",
        position: "relative",
      }}
    >
      <span
        style={{
          textAlign: `${alignRight ? "left" : undefined}`,
          position: "absolute",
          top: -26,
          right: alignRight ? 25 : undefined,
          fontSize: "32px",
          width: "275px",
          background: isDebug ? "grey" : "transparent",
          color: "white",
          zIndex: 0,
        }}
      >
        {`{{`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;sign&nbsp;&nbsp;|&nbsp;&nbsp;
        {signingIndex}&nbsp;&nbsp;|&nbsp;*&nbsp;
        {`}}`}
      </span>
      <u style={{ marginLeft: `${alignRight ? "auto" : undefined}` }}>
        __________________________________
      </u>
      <div>
        {signReplaceComponent}
        <div>{signerSubText}</div>
      </div>
    </p>
  );
};
